import PapaParse from 'papaparse';
import { CONFIG } from './parse_config';
import { cleanDollarField, cleanMultipleLinesField } from './returns_csv_helper';

export default function parseQuestionsFile(text) {
    /**
     * Reads a txt file and parses it based on the tags [YEAR], [TOPIC] and [QUESTION].
     * if the tag [YEAR] is found the following text until [TOPIC] or [QUESTION] is the year.
     * if the tag [TOPIC] is found the following text until [QUESTION] is the topic.
     * if the tag [QUESTION] is found the following text until [QUESTION] or [TOPIC] or end of file is the question.
     * 
     * at the end of each question text a new question should be created and appended to the list of questions.
     * question objects will be of the form {question: string, topic: string, year: string}
     * the topic will be the most recently seen topic tag.
     * the year will be the most recently seen year tag.
     * 
     * Example:
     * [YEAR] 2019
     * [TOPIC] PERSONAL
     *    [QUESTION] What is your date of birth
     *    [QUESTION] What is your middle name (if any)
     * 
     * @param {string} text - The text to parse.
     * @returns {Array<Object>} An array of questions.
     */
    const questions = [];
    let currentTopic = null;
    let currentQuestion = null;
    let questionMode = false
    let topicMode = false

    // Split text into lines and filter out any empty ones
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    for (const line of lines) {
        if ((line.startsWith('[TOPIC]') || line.startsWith('[QUESTION]'))
            && currentQuestion && currentTopic) {
            questions.push({
                topic: currentTopic.trim(),
                question: currentQuestion.trim()
            });
            currentQuestion = null;
        }
        // Check for [TOPIC] tag
        if (line.startsWith('[TOPIC]')) {
            // Extract text after [TOPIC]
            currentTopic = line.replace('[TOPIC]', '').trim();
            questionMode = false;
            topicMode = true;
        }
        // Check for [QUESTION] tag
        else if (line.startsWith('[QUESTION]')) {
            // Extract text after [QUESTION]
            currentQuestion = line.replace('[QUESTION]', '').trim();
            questionMode = true;
            topicMode = false;
        } else {
            if (questionMode && currentQuestion !== null) {
                currentQuestion += '\n' + line.trim();
            } else if (topicMode && currentTopic !== null) {
                currentTopic += '\n' + line.trim();
            }
        }
    }

    // Push the final question — the loop only pushes on the *next* tag,
    // so the last question in the file would otherwise be lost.
    if (currentQuestion && currentTopic) {
        questions.push({
            topic: currentTopic.trim(),
            question: currentQuestion.trim()
        });
    }

    return questions;
}

export function makeKey(name, email) { return `${(name || '').trim().toLowerCase()} ${(email || '').trim().toLowerCase()}` }

export function getParsedReturnsObjects(parsedLines) {
    let headers = []
    let returns = []
    let clients = new Map();
    try {
        for (let splitLine of parsedLines) {
            if (headers.length === 0) {
                if (String(splitLine[0]).trim().toLowerCase() === 'no.') {
                    headers = splitLine.map(header => header.trim())
                }
            } else {
                const clientObj = {
                    name: (splitLine[headers.findIndex(header => header.toLowerCase().includes('name'))] || '').trim(),
                    // auth rep and mycra should be boolean true if not empty
                    authorized_representative: (splitLine[headers.findIndex(header => header.toLowerCase().includes('a/r'))] || '').trim().toLowerCase().includes('yes'),
                    myCRA: (splitLine[headers.findIndex(header => header.toLowerCase().includes('mycra'))] || '').trim().toLowerCase().includes('yes'),

                    telephone: cleanMultipleLinesField(splitLine[headers.findIndex(header => header.toLowerCase().includes('telephone'))] || ''),
                    emails: cleanMultipleLinesField(splitLine[headers.findIndex(header => header.toLowerCase().includes('e-mail') || header.toLowerCase().includes('email'))] || ''),
                    title: (splitLine[headers.findIndex(header => header.toLowerCase().includes('title'))] || '').trim(),
                    language: (splitLine[headers.findIndex(header => header.toLowerCase().includes('language'))] || '').trim(),
                    date_of_birth: (splitLine[headers.findIndex(header => header.toLowerCase().includes('birth'))] || '').trim(),
                    marital_status: (splitLine[headers.findIndex(header => header.toLowerCase().includes('marital'))] || '').trim(),
                    spouse_name: (splitLine[headers.findIndex(header => header.toLowerCase().includes('spouse'))] || '').trim(),
                    citizenship_status: (splitLine[headers.findIndex(header => header.toLowerCase().includes('citizen'))] || '').trim().toLowerCase().includes("yes"),
                    address: (splitLine[headers.findIndex(header => header.toLowerCase().includes('address') && !header.toLowerCase().includes('email') && !header.toLowerCase().includes('e-mail'))] || '').trim(),
                    sin: (splitLine[headers.findIndex(header => header.toLowerCase().includes('sin number'))] || '').trim(),
                }
                const clientID = makeKey(clientObj.name, clientObj.emails)
                const statusIdx = headers.findIndex(header => header.toLowerCase() === 'status');
                const rawStatus = statusIdx !== -1 ? (splitLine[statusIdx] || '').trim() : '';
                const ALLOWED_STATUSES = ['Open', 'Closed', 'Pending', 'Awaiting Documents'];
                const matchedStatus = ALLOWED_STATUSES.find(s => s.toLowerCase() === rawStatus.toLowerCase()) || 'Open';

                const returnObj = {
                    clientID: clientID,
                    year: (splitLine[headers.findIndex(header => header.toLowerCase() === 'year')] || '').trim(),
                    status: matchedStatus,
                    fees: headers.reduce((acc, header, index) => {
                        if (header.toLowerCase().includes('fee')) {
                            acc[header] = cleanDollarField(splitLine[index])
                        }
                        return acc
                    }, {}),
                    cash: cleanDollarField(splitLine[headers.findIndex(header => header.toLowerCase().includes('cash'))] || ''),
                    e_transfer_or_cheque: cleanDollarField(splitLine[headers.findIndex(header => header.toLowerCase().includes('transfer') || header.toLowerCase().includes('cheque'))] || ''),
                    outstanding_balance: cleanDollarField(splitLine[headers.findIndex(header => header.toLowerCase().includes('standing'))] || ''),
                    invoice_number: splitLine[headers.findIndex(header => header.toLowerCase().includes('invoice'))] || '',
                    last_contact_date: splitLine[headers.findIndex(header => header.toLowerCase().includes('contact'))] || '',
                    notes: splitLine[headers.findIndex(header => header.toLowerCase().includes('comments'))] || '',

                }
                if (!clients.has(clientID)) {
                    clients.set(clientID, clientObj);
                }
                returns.push(returnObj);
            }

        }
    } catch (error) {
        return { error: 'Error parsing CSV: ' + error.message };
    }

    console.log(clients)
    const duplicates = returns.filter((returnObj, index) => {
        return returns.findIndex((r) => {
            return r.clientID === returnObj.clientID && r.year === returnObj.year && r.invoice_number === returnObj.invoice_number
        }) !== index
    })
    return { clients: Array.from(clients.values()), returns: returns, duplicates: duplicates }
}


export function papaParseCSV(text) {
    /**
     * Reads a csv file and parses it into generic return objects.
     */
    try {
        const csvData = PapaParse.parse(text, CONFIG);
        if (csvData.errors.length > 0) {
            return { error: 'Error parsing CSV: ' + csvData.errors.join(', ') };
        }
        return csvData.data;
    } catch (error) {
        return { error: 'Error parsing CSV: ' + error.message };
    }
}


